import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import utc from 'dayjs/plugin/utc';

import { isDateValid } from './isDateValid';

dayjs.extend(utc);
dayjs.extend(customParseFormat);

const supportedDateTimeFormatPartType = [
  'day',
  'month',
  'year',
  'literal',
] as const;
type SupportedDateTimeFormatPartType =
  (typeof supportedDateTimeFormatPartType)[number];

function isSupportedDateTimeFormatPartType(
  type: keyof Intl.DateTimeFormatPartTypesRegistry
): type is SupportedDateTimeFormatPartType {
  return (supportedDateTimeFormatPartType as ReadonlyArray<string>).includes(
    type
  );
}

function assertValidConfig(
  parts: Intl.DateTimeFormatPart[],
  options: Intl.ResolvedDateTimeFormatOptions
) {
  // Literals supported by dayjs custom format parser. See: https://day.js.org/docs/en/parse/string-format#list-of-all-recognized-separator-characters
  const supportedLiterals = new Set('-_:.,()/');
  const supportedFormats = new Set(['numeric', '2-digit']);

  if (
    !parts.every((part) => {
      const { type } = part;
      return (
        isSupportedDateTimeFormatPartType(type) &&
        ((type === 'literal' && supportedLiterals.has(part.value.trim())) ||
          (type !== 'literal' && supportedFormats.has(options[type] ?? '')))
      );
    })
  ) {
    throw new Error('Not supported part');
  }
}

function combineParts(
  formatStringParts: (string | string[])[],
  prefix = ''
): string[] {
  if (formatStringParts.length > 0) {
    const [cur, ...rest] = formatStringParts;
    return Array.isArray(cur)
      ? cur.flatMap((c) => combineParts(rest, `${prefix}${c}`))
      : combineParts(rest, `${prefix}${cur}`);
  }

  return [prefix];
}

function getFormatStrings(
  parts: Intl.DateTimeFormatPart[],
  options: Intl.ResolvedDateTimeFormatOptions
): string[] {
  assertValidConfig(parts, options);

  const formatStringParts = parts.map((part) => {
    switch (part.type) {
      case 'day':
        return ['D', 'DD'];
      case 'month':
        return ['M', 'MM'];
      case 'year':
        return ['YY', 'YYYY'];
      case 'literal':
        return part.value;
      default:
        return '';
    }
  });

  return combineParts(formatStringParts);
}

function applyFormatStrings(dateString: string, formatStrings: string[]) {
  if (formatStrings.length > 0) {
    const [cur, ...rest] = formatStrings;
    const parsedDate = dayjs(dateString, cur, true).toDate();

    if (isDateValid(parsedDate)) {
      return parsedDate;
    }

    return applyFormatStrings(dateString, rest);
  }

  return new Date('invalid date');
}

export function getDateStringParser(dateTimeFormat: Intl.DateTimeFormat) {
  const resolvedOptions = dateTimeFormat.resolvedOptions();
  const parts = dateTimeFormat.formatToParts();

  if (resolvedOptions.numberingSystem !== 'latn') {
    throw new Error('Not supported numbering system.');
  }

  if (resolvedOptions.calendar !== 'gregory') {
    throw new Error('Not supported calender.');
  }

  const formatStrings = getFormatStrings(parts, resolvedOptions);

  return (dateString: string) => {
    return applyFormatStrings(dateString, formatStrings);
  };
}
